import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  NgZone,
  OnDestroy,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { EventEmitter } from '@angular/forms/src/facade/async';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/distinctUntilChanged';

declare const google: any;

export interface Result {
  placeGoolge: any;
  latitude: number;
  longitude: number;
  streetaddress: string;
  location: string;
  phonenumber: any;
  city: string;
  state: string;
  zip: string;
  county: string;
  street_number: any;
  route: string;
  country: any;
  postal_code: any;
}

@Component({
  selector: 'app-g-auto-complete',
  templateUrl: './g-auto-complete.component.html',
  styleUrls: ['./g-auto-complete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GAutoCompleteComponent),
      multi: true
    }
  ]
})
export class GAutoCompleteComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy, ControlValueAccessor {
  @Input() googleId: string;
  @Output() emitterResult: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('autoCompleteInputGoogle') inputEl: ElementRef;

  result: Result;
  private placesLibrary: any = null;
  private sessionToken: any = null;
  suggestions: Array<{ placePrediction?: any }> = [];
  selectedIndex = 0;
  private inputSubject: Subject<string> = new Subject<string>();
  private inputSub: Subscription;
  private _googleOdrdGuid: string;
  private componentLocation: any = {
    street_number: 'short_name',
    route: 'short_name',
    locality: 'short_name',
    administrative_area_level_1: 'short_name',
    country: 'long_name',
    postal_code: 'short_name',
    administrative_area_level_2: 'short_name',
    neighborhood: 'long_name',
  };
  propagateChange: any = () => {};

  constructor(private zone: NgZone) {
    this.initResult();
  }

  get hasSuggestions(): boolean {
    return !!(this.suggestions && this.suggestions.length);
  }

  ngOnInit() {
    this.loadPlacesLibrary();
    this.inputSub = this.inputSubject
      .distinctUntilChanged()
      .subscribe((value: string) => {
        this.fetchSuggestions(value);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['googleId'] &&
      changes['googleId'].currentValue !== changes['googleId'].previousValue
    ) {

      this._googleOdrdGuid = changes['googleId'].currentValue;
      this.applyExperienceIds();
    }
  }

  ngAfterViewInit() {
    this.applyExperienceIds();
  }

  private async loadPlacesLibrary() {
    if (
      !google ||
      !google.maps ||
      typeof google.maps.importLibrary !== 'function'
    ) {
      return;
    }
    this.placesLibrary = await google.maps.importLibrary('places');
    this.sessionToken = new this.placesLibrary.AutocompleteSessionToken();
  }

  private applyExperienceIds() {
    if (this._googleOdrdGuid && google.maps.Settings.getInstance) {
      google.maps.Settings.getInstance().experienceIds = [this._googleOdrdGuid];
    }
  }

  onFocus() {
    if (!this.placesLibrary) {
      return;
    }
    this.sessionToken = new this.placesLibrary.AutocompleteSessionToken();

    const value = (this.inputEl.nativeElement.value || '').trim();
    if (value && !this.suggestions.length) {
      this.fetchSuggestions(value);
    }
  }

  onBlur() {
    this.zone.run(() => {
      this.suggestions = [];
      this.selectedIndex = 0;
    });
  }

  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = (input && input.value ? input.value : '').trim();

    if (!value) {
      this.zone.run(() => {
        this.suggestions = [];
        this.selectedIndex = 0;
      });
      return;
    }

    this.inputSubject.next(value);
  }

  onKeydown(event: KeyboardEvent) {
    if (!this.suggestions.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectedIndex = Math.min(
        this.selectedIndex + 1,
        this.suggestions.length - 1,
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
    } else if (event.key === 'Enter' && this.suggestions[this.selectedIndex]) {
      event.preventDefault();
      this.selectSuggestion(this.suggestions[this.selectedIndex]);
    } else if (event.key === 'Escape') {
      this.zone.run(() => {
        this.suggestions = [];
        this.selectedIndex = 0;
      });
    }
  }

  isSuggestionSelected(index: number): boolean {
    return index === this.selectedIndex;
  }

  getSuggestionMainText(s: { placePrediction?: any }): string {
    const main =
      s &&
      s.placePrediction &&
      s.placePrediction.mainText &&
      s.placePrediction.mainText.text;
    if (main != null) {
      return main;
    }
    return (
      (s &&
        s.placePrediction &&
        s.placePrediction.text &&
        s.placePrediction.text.text) ||
      ''
    );
  }

  getSuggestionSecondaryText(s: { placePrediction?: any }): string {
    return (
      (s &&
        s.placePrediction &&
        s.placePrediction.secondaryText &&
        s.placePrediction.secondaryText.text) ||
      ''
    );
  }

  private async fetchSuggestions(input: string) {
    if (!this.placesLibrary || !input.trim()) {
      return;
    }

    const resp =
      await this.placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        {
          input: input.trim(),
          sessionToken: this.sessionToken || undefined,
          includedRegionCodes: ['us'],
        },
      );

    this.zone.run(() => {
      this.suggestions = resp && resp.suggestions ? resp.suggestions : [];
      this.selectedIndex = 0;
    });
  }

  async selectSuggestion(suggestion: { placePrediction?: any }) {
    const pred = suggestion && suggestion.placePrediction;
    if (!pred || !pred.toPlace || !this.placesLibrary) {
      return;
    }

    this.zone.run(() => {
      this.suggestions = [];
      this.selectedIndex = 0;
    });

    const place = await pred.toPlace();

    await place.fetchFields({
      fields: [
        'displayName',
        'formattedAddress',
        'location',
        'addressComponents',
        'internationalPhoneNumber',
      ],
    });

    this.zone.run(() => {
      const input =
        this.inputEl && (this.inputEl.nativeElement as HTMLInputElement);
      if (input) {
        const v =
          place.formattedAddress ||
          (place.displayName && place.displayName.text) ||
          place.displayName ||
          '';
        input.value = v;
      }
      this.buildJson(place);

      this.sessionToken = new this.placesLibrary.AutocompleteSessionToken();
    });
  }

  initResult() {
    this.result = {
      placeGoolge: {},
      latitude: undefined,
      longitude: undefined,
      streetaddress: '',
      location: '',
      phonenumber: '',
      city: '',
      state: '',
      zip: '',
      county: '',
      street_number: '',
      route: '',
      country: '',
      postal_code: '',
    };
  }

  buildJson(place: any) {
    if (!place || !place.location) {
      return;
    }

    this.initResult();
    this.result.placeGoolge = place;

    const lat =
      typeof place.location.lat === 'function'
        ? place.location.lat()
        : place.location.lat;
    const lng =
      typeof place.location.lng === 'function'
        ? place.location.lng()
        : place.location.lng;
    this.result.latitude = lat;
    this.result.longitude = lng;


    const rawPhone = place.internationalPhoneNumber || '';
    const clean = rawPhone ? rawPhone.replace(/\D/g, '') : '';
    const us10 =
      clean.length === 11 && clean.startsWith('1') ? clean.substring(1) : clean;

    this.result.phonenumber = us10 || '';

    const formatted = place.formattedAddress || '';
    const displayName =
      place.displayName && place.displayName.text
        ? place.displayName.text
        : place.displayName || '';

    this.result.location = displayName || formatted || '';

    const rawAddressComponents = place.addressComponents;
    if (rawAddressComponents == null) {
      this.result.city = null;
      this.result.state = null;
      this.result.county = null;
      this.result.street_number = null;
      this.result.route = null;
      this.result.country = null;
      this.result.postal_code = null;
      this.result.zip = null;
    }

    const addressComponents = rawAddressComponents || [];
    let route = '';
    let streetNumber = '';


    for (let i = 0; i < addressComponents.length; i++) {
      const comp = addressComponents[i];
      const types = comp && comp.types ? comp.types : [];
      const addressType = types[0];
      if (!addressType) {
        continue;
      }

      const longText = comp.longText || '';
      const shortText = comp.shortText || '';
      const pref = this.componentLocation[addressType];
      let val = pref === 'long_name' ? longText : shortText;
      if (!val) {
        val = longText || shortText || '';
      }

      switch (addressType) {
        case 'locality':
        case 'neighborhood':
          this.result.city = val;
          break;
        case 'administrative_area_level_1':
          this.result.state = val;
          break;
        case 'administrative_area_level_2':
          this.result.county = val;
          break;
        case 'street_number':
          streetNumber = val;
          this.result.street_number = val;
          break;
        case 'route':
          route = val;
          this.result.route = val;
          break;
        case 'country':
          this.result.country = val;
          break;
        case 'postal_code':
          this.result.postal_code = val;
          this.result.zip = val;
          break;
      }
    }

    const street =
      streetNumber && route
        ? streetNumber + ' ' + route
        : route || streetNumber || '';
    this.result.streetaddress =
      street || formatted || this.result.location || '';

    this.emitterResult.emit(this.result);
    this.propagateChange(this.result);
  }

  writeValue(value: any) {
    const input =
      this.inputEl && (this.inputEl.nativeElement as HTMLInputElement);
    if (input) {
      input.value = value || '';
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {}

  ngOnDestroy(): void {
    if (this.inputSub) {
      this.inputSub.unsubscribe();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/A2-utils/t2-autocomplete-location/g-auto-complete/g-auto-complete.component.ts